import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import PageHead from "@/components/feature/PageHead";

const COMPANY_NAME = "NetBasis International";
const title = "Interim Management";
const heroImage = "https://net-basis.net/Bilder/ict-interim-management-experts-dach-market-entry.webp";
const heroAlt = "Un empresario elige directivos TIC expertos para dirigir ventas y proyectos en el mercado DACH.";
const heroClaimLine1 = "¡Inmediato, flexible, experto, experimentado ... temporal!";
const benefitsTitle = "Cómo se beneficia de nuestra experiencia en __interim management__:";
const bottomClaim = "NetBasis International - drive to excellence with excellent drivers !";

const widget2Text =
  "Su socio experimentado para un Interim Management flexible durante la entrada al mercado de la región DACH. Apoyamos a los fabricantes extranjeros de TIC sobre el terreno según sus necesidades y asumimos áreas de actividad específicas o la totalidad del proyecto internacional.";

const p1Text =
  "Diversas razones pueden hacer que el tema del \"Interim Management\" sea importante para una empresa al ingresar a nuevos mercados. El objetivo estratégico de expandir el negocio a nuevos países debe llevarse a cabo de manera independiente de las operaciones existentes. La misión debe abordarse de forma rápida, flexible y según la demanda, y está definida desde el principio solo para un período limitado. La adaptación a nuevos mercados y la búsqueda de una posición exitosa en el mercado requieren conocimientos, habilidades y experiencias específicas que los propios empleados a menudo no pueden o no deben cumplir.";
const p2Text =
  "Precisamente aquí es donde NetBasis International ayuda a sus socios fabricantes extranjeros a alcanzar los objetivos internacionales deseados o definidos con precisión dentro de un plazo fijo. Al hacerlo, no solo asumimos áreas de actividad individuales (ver desarrollo de negocio), sino también, si lo desean, la totalidad del proyecto internacional.";
const p2LinkText = "desarrollo de negocio";
const p2LinkUrl = "/es/servicios/desarrollo-de-negocio";

const benefits = [
  "Presencia rápida sobre el terreno con un mínimo esfuerzo de contratación",
  "Objetividad absoluta para su proyecto internacional específico",
  "Sólida experiencia en gestión y competencia operativa",
  "Profundos conocimientos del mercado, habilidades y contactos directos",
  "Trabajo enfocado y orientado a resultados para alcanzar sus objetivos internacionales",
  "Relaciones contractuales sencillas gracias a plazos de disponibilidad y rescisión breves",
  "Sin competencia ni conflictos para los ejecutivos internos",
  "Valiosos efectos de desarrollo de personal según el proyecto",
  "Visibilidad absoluta de los costes incurridos y de nuestra remuneración",
];

export default function InterimManagementEsPage() {
  const strongName = COMPANY_NAME;
  const p1Parts = p1Text.split(strongName);

  return (
    <div className="min-h-screen flex flex-col">
      <PageHead
        title="Gestión Interina | NetBasis International"
        description="Experiencia de liderazgo temporal cualificada para cubrir vacantes o gestionar fases críticas de expansión."
        ogUrl="https://net-basis.net/es/servicios/interim-management"
      />
      <Header />

      <main className="flex-1 pt-20 md:pt-24">
        {/* Hero image */}
        <section className="w-full bg-white py-4 md:py-6 lg:py-8">
          <div className="w-full max-w-[1100px] mx-auto">
            <div className="relative w-full h-[320px] md:h-[420px] lg:h-[500px] overflow-hidden">
              <img loading="lazy"
                src={heroImage}
                alt={heroAlt}
                title={title}
                className="absolute inset-0 w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/50"></div>

              <div className="absolute inset-0 z-20 flex flex-col items-start px-6 md:px-10 lg:pl-14 pointer-events-none mt-5 md:mt-10 lg:mt-10">
                <p
                  className="text-3xl md:text-4xl lg:text-5xl font-normal text-white drop-shadow-[0_1px_4px_rgba(0,0,0,0.6)] text-left animate-[interimTextSlide_12s_ease-in-out_infinite]"
                  style={{
                    fontFamily: '"Open Sans", sans-serif',
                  }}
                >
                  {heroClaimLine1}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="py-6 md:py-8 px-4 md:px-6 lg:px-8 bg-white">
          <div className="max-w-5xl mx-auto">
            <h2 className="font-heading text-lg md:text-xl font-bold text-primary-600 mb-5 md:mb-7">
              {title}
            </h2>

            <h1 className="text-foreground-950 leading-relaxed text-sm md:text-base font-semibold mb-4">
              {widget2Text}
            </h1>

            <div className="space-y-4 text-foreground-950 leading-relaxed text-sm md:text-base">
              <p>
                {p1Parts.map((part, i) => (
                  <span key={i}>
                    {part}
                    {i < p1Parts.length - 1 && (
                      <strong className="text-primary-600">{strongName}</strong>
                    )}
                  </span>
                ))}
              </p>
              <p>
                {(() => {
                  const parts = p2Text.split(strongName);
                  return parts.map((part, i) => {
                    const linkIdx = part.indexOf(p2LinkText);
                    if (linkIdx !== -1) {
                      const before = part.slice(0, linkIdx);
                      const after = part.slice(linkIdx + p2LinkText.length);
                      return (
                        <span key={i}>
                          {before}
                          <a href={p2LinkUrl} className="text-primary-600 hover:underline">{p2LinkText}</a>
                          {after}
                        </span>
                      );
                    }
                    return (
                      <span key={i}>
                        {part}
                        {i < parts.length - 1 && <strong className="text-primary-600">{strongName}</strong>}
                      </span>
                    );
                  });
                })()}
              </p>
            </div>

            {/* Benefits heading with italic word */}
            <h2 className="font-heading text-lg md:text-xl font-bold text-primary-600 mt-8 md:mt-10 mb-5 md:mb-7">
              {benefitsTitle.split("__").map((part, i) =>
                i % 2 === 1 ? <em key={i} className="mr-[0.15em]">{part}</em> : part
              )}
            </h2>

            {/* Benefits with checkmarks */}
            <div className="space-y-1.5 md:space-y-2 text-foreground-950 leading-relaxed text-sm md:text-base">
              {benefits.map((benefit, idx) => (
                <div key={idx} className="flex items-start gap-2 md:gap-3">
                  <div className="w-5 h-5 flex-shrink-0 flex items-center justify-center mt-0.5">
                    <i className="ri-check-line text-primary-600 text-base md:text-lg"></i>
                  </div>
                  <p>{benefit}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Bottom claim */}
        <section className="py-6 md:py-10 px-4 md:px-6 lg:px-8 bg-white">
          <div className="max-w-5xl mx-auto text-center">
            <p className="font-heading text-lg md:text-xl font-bold text-primary-600">
              {bottomClaim}
            </p>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}